/**
 * First-run setup: which steps a person needs on this device, by role,
 * platform and account flags. Pure and framework-free — the app's setup flow,
 * the device report the app sends, the server's setup-health rollup and the
 * admin user view all read the same matrix.
 *
 * Levels:
 * - `required` — comes back (cold start / back after 15+ min away) until done.
 * - `optional` — asked once per device; "Not now" is final until an admin
 *   reset or a `SETUP_FLOW_VERSION` bump.
 * - `info`     — an explainer, shown once per user, done when read.
 *
 * Steps that cannot apply on a platform are left out (never shown as "N/A").
 */

import {
  isIndependentUser,
  isMember,
  isProvider,
  resolveProviderLevel,
  type RoleProfile,
} from '../rbac/member-roles'

/** Bump to show setup again to everyone (existing completions are void). */
export const SETUP_FLOW_VERSION = 1

export const SETUP_STEP_IDS = [
  'notifications',
  'calls-channel',
  'lock-screen-calls',
  'battery',
  'microphone',
  'camera',
  'location',
  'location-precise',
  'recording-consent',
  'session-recording-info',
  'app-lock',
] as const

/** Steps in the order setup walks them. */
export type SetupStepId = (typeof SETUP_STEP_IDS)[number]

export type SetupLevel = 'required' | 'optional' | 'info'

export type SetupRequirement = { id: SetupStepId; level: SetupLevel }

/** Who the matrix treats someone as. `null` = role not known yet. */
export type SetupAudience = 'member' | 'independent' | 'provider'

export type SetupPlatform = 'ios' | 'android' | 'web'

/** Steps stored on the account (cross-device); every other step is per device. */
export const USER_SCOPED_SETUP_STEPS = ['recording-consent', 'session-recording-info'] as const
export type UserScopedSetupStepId = (typeof USER_SCOPED_SETUP_STEPS)[number]

export function isUserScopedSetupStep(id: SetupStepId): id is UserScopedSetupStepId {
  return (USER_SCOPED_SETUP_STEPS as readonly string[]).includes(id)
}

/**
 * The device-permission steps, in setup order — what an admin "Reset device
 * permissions" / per-row "Ask again" may target. Everything per-device:
 * the user-scoped steps (call recording, the session-recording explainer)
 * go through Resend consent / Reset setup instead.
 */
export const DEVICE_SETUP_STEPS = SETUP_STEP_IDS.filter(
  (id) => !isUserScopedSetupStep(id),
) as readonly SetupStepId[]

export function isDeviceSetupStep(value: unknown): value is SetupStepId {
  return isSetupStepId(value) && !isUserScopedSetupStep(value)
}

export function isSetupStepId(value: unknown): value is SetupStepId {
  return typeof value === 'string' && (SETUP_STEP_IDS as readonly string[]).includes(value)
}

/** Android 14 (API 34) is where full-screen call alerts became a user setting. */
export const ANDROID_FULL_SCREEN_INTENT_MIN_SDK = 34

export type SetupContext = {
  role: RoleProfile
  platform: SetupPlatform
  /** Android API level, when known. Unknown keeps SDK-gated steps (the probe decides). */
  androidSdk?: number | null
  /**
   * The account may make and get calls. Members and Independents: their
   * account permission `calls` is not `disabled` (default enabled). Ignored
   * for providers (always true).
   */
  callsEnabled: boolean
  /** Independent marked "Can give consent" on at least one association. */
  consentGiver: boolean
  /** Holds Premium Session Recording (only counts on a provider role). */
  premiumSessionRecording: boolean
  /** Web only: this browser can receive web push (false on iPhone/iPad Safari off the Home Screen). */
  webPushSupported: boolean
  /** Native probes this app build ships (feature-detected; added after Android 133 / iOS 98). */
  nativeProbes: { callsChannel: boolean; iosPrecise: boolean }
  /** Biometric app lock is possible on this device. Defaults to true on native. */
  appLockSupported?: boolean
}

export function setupAudienceFor(role: RoleProfile | null | undefined): SetupAudience | null {
  if (!role) return null
  if (isProvider(role)) return 'provider'
  if (isIndependentUser(role)) return 'independent'
  if (isMember(role)) return 'member'
  // Staff rows without "provider" in the role string (legacy owner / admin labels).
  if ((role.roleType ?? '').trim() && resolveProviderLevel(role) >= 2) return 'provider'
  return null
}

/**
 * The call-recording step's variant: Members/Independents answer Yes/No
 * (their calls depend on it); providers read the notice ("I understand").
 */
export function recordingConsentStepVariant(
  role: RoleProfile | null | undefined,
): 'consent' | 'acknowledge' | null {
  const audience = setupAudienceFor(role)
  if (!audience) return null
  return audience === 'provider' ? 'acknowledge' : 'consent'
}

export type SessionRecordingExplainerVariant = 'member' | 'consent-giver' | 'psr'

/** Bump a variant's suffix to show that explainer once more. */
export const SESSION_RECORDING_EXPLAINER_VERSIONS: Record<SessionRecordingExplainerVariant, string> = {
  member: 'member-v1',
  'consent-giver': 'consent-giver-v1',
  psr: 'psr-v1',
}

export function sessionRecordingExplainerVariant(
  ctx: Pick<SetupContext, 'role' | 'consentGiver' | 'premiumSessionRecording'>,
): SessionRecordingExplainerVariant | null {
  const audience = setupAudienceFor(ctx.role)
  if (audience === 'member') return 'member'
  if (audience === 'independent') return ctx.consentGiver ? 'consent-giver' : null
  if (audience === 'provider') return ctx.premiumSessionRecording ? 'psr' : null
  return null
}

/** The stored marker (`setupState.explainersSeen.sessionRecording`) this person needs. */
export function sessionRecordingExplainerVersion(
  ctx: Pick<SetupContext, 'role' | 'consentGiver' | 'premiumSessionRecording'>,
): string | null {
  const variant = sessionRecordingExplainerVariant(ctx)
  return variant ? SESSION_RECORDING_EXPLAINER_VERSIONS[variant] : null
}

/**
 * The applicable steps, in setup order. Empty while the role is unknown —
 * setup never runs before the role has loaded.
 */
export function setupRequirements(ctx: SetupContext): SetupRequirement[] {
  const audience = setupAudienceFor(ctx.role)
  if (!audience) return []

  const provider = audience === 'provider'
  const native = ctx.platform !== 'web'
  const android = ctx.platform === 'android'
  const canCall = provider || ctx.callsEnabled
  /** Required for anyone who can be called; asked once when an admin has switched calls off. */
  const callLevel: SetupLevel = canCall ? 'required' : 'optional'
  const sdkAtLeast = (min: number) => ctx.androidSdk == null || ctx.androidSdk >= min

  const out: SetupRequirement[] = []
  const add = (id: SetupStepId, level: SetupLevel, when = true) => {
    if (when) out.push({ id, level })
  }

  add('notifications', 'required', native || ctx.webPushSupported)
  add('calls-channel', callLevel, android && ctx.nativeProbes.callsChannel)
  add('lock-screen-calls', callLevel, android && sdkAtLeast(ANDROID_FULL_SCREEN_INTENT_MIN_SDK))
  add('battery', provider ? 'required' : 'optional', android)
  add('microphone', callLevel)
  add('camera', provider ? 'required' : 'optional')
  add('location', 'required', provider && native)
  add(
    'location-precise',
    'required',
    provider && (android || (ctx.platform === 'ios' && ctx.nativeProbes.iosPrecise)),
  )
  // Owner decision: someone whose calling is switched off isn't asked about call
  // recording until calling is switched on again (then it's required).
  add('recording-consent', 'required', canCall)
  add('session-recording-info', 'info', sessionRecordingExplainerVariant(ctx) !== null)
  add('app-lock', 'optional', native && (ctx.appLockSupported ?? true))

  return out
}

/** Short labels for lists (admin view, reminder lists, device reports). */
export const SETUP_STEP_LABELS: Record<SetupStepId, string> = {
  notifications: 'Alerts',
  'calls-channel': 'Calls ring',
  'lock-screen-calls': 'Lock-screen calls',
  battery: 'Keep calls working',
  microphone: 'Microphone',
  camera: 'Camera',
  location: 'Location',
  'location-precise': 'Precise location',
  'recording-consent': 'Call recording',
  'session-recording-info': 'Recording a session',
  'app-lock': 'App lock',
}
